import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { CancerStages, DoseCalcModeActive, VolumeMode } from '../../database/schema.js';
import { zql } from '../../schema.js';
import { Status } from '../../util/schemas.js';
import { EventLogs } from '../../utils.js';
import { EventLogResources, addEventLog } from '../util.js';
import { changedItems, itemsDetail, regularDoseUnit, valueItem } from '../../util/event-log-items.js';
import { protocolDayItems } from '../patient/medicine-log-items.js';
const AgeRangeSchema = z.object({
    minAge: z.number(),
    maxAge: z.number(),
    maxDose: z.number(),
});
export const MedicineSchema = z.object({
    id: z.string().optional(),
    activeSubstanceId: z.array(z.string()),
    prescribedNameId: z.array(z.string()),
    autIdem: z.boolean().default(false),
    calculationMethod: z.array(DoseCalcModeActive),
    notes: z.string().optional(),
    regularDose: z.number(),
    precisionDecimals: z.number(),
    doseProportion: z.number().min(0).max(100).optional(),
    ageRanges: z.array(AgeRangeSchema).optional(),
});
const TherapySequenceSchema = z.object({
    id: z.string(),
    day: z.number().min(0),
    time: z.string().min(1),
    isNote: z.boolean().default(false),
    note: z.string().optional(),
    carrierSolutionId: z.array(z.string()),
    applicationFormId: z.array(z.string()),
    duration: z.number().min(0),
    volume: z.number().min(0),
    volumeMode: z.array(VolumeMode),
    medicine: z.array(MedicineSchema),
    therapyNote: z.string().nullable().optional(),
    pharmacyNote: z.string().nullable().optional(),
});
export const ProtocolSchema = z.object({
    name: z.string().min(1),
    stage: z.array(CancerStages),
    diagnosisId: z.uuidv7().nullable().optional(),
    cancerTypeId: z.uuidv7().nullable().optional(),
    isTrial: z.boolean().default(false),
    notes: z.string().optional(),
    totalCycles: z.number().min(0).optional(),
    daysRepetition: z.number().min(0).optional(),
    therapySequence: z.array(TherapySequenceSchema).optional(),
});
export const AddProtocolSchema = ProtocolSchema;
export const UpdateProtocolSchema = AddProtocolSchema.partial().extend({ id: z.uuidv7() });
export const UpdateProtocolBatchStatusSchema = z.object({ protocolIds: z.array(z.uuidv7()), status: Status });
export const DeleteProtocolBatchSchema = z.object({ protocolIds: z.array(z.uuidv7()) });
const presentIds = (ids) => [...new Set(ids.filter((id) => Boolean(id)))];
/** Drug, trade name and application form names for protocol logs, loaded once per call. */
async function loadProtocolLogNames(tx, ids) {
    const drugIds = presentIds(ids.drugIds);
    const tradeNameIds = presentIds(ids.tradeNameIds);
    const formIds = presentIds(ids.formIds);
    const drugs = drugIds.length
        ? await tx.run(zql.drugCatalogItems.where('id', 'IN', drugIds).related('activeSubstanceDetails', d => d.related('doseUnit')))
        : [];
    const tradeNames = tradeNameIds.length ? await tx.run(zql.prescribedNames.where('id', 'IN', tradeNameIds)) : [];
    const forms = formIds.length ? await tx.run(zql.applicationForms.where('id', 'IN', formIds)) : [];
    const drugName = (id) => drugs.find(drug => drug.id === id)?.name ?? null;
    return {
        drugName,
        formName: id => forms.find(form => form.id === id)?.name ?? null,
        medicineName: medicine => tradeNames.find(trade => trade.id === medicine.prescribedNameId)?.name ??
            drugName(medicine.activeSubstanceId) ??
            '-',
        doseUnit: (activeSubstanceId, method) => regularDoseUnit(method, drugs.find(drug => drug.id === activeSubstanceId)?.activeSubstanceDetails?.doseUnit?.displayName ?? 'mg'),
    };
}
export async function updateProtocol(tx, userId, input) {
    const prev = await tx.run(zql.protocols.where('id', '=', input.id).one());
    if (!prev)
        throw new Error('Not Found');
    const now = Date.now();
    const updatedProtocol = {
        name: input.name,
        stage: input.stage !== undefined ? (input.stage[0] ?? null) : prev.stage,
        diagnosisId: input.diagnosisId !== undefined ? input.diagnosisId : prev.diagnosisId,
        cancerTypeId: input.cancerTypeId !== undefined ? input.cancerTypeId : prev.cancerTypeId,
        isTrial: input.isTrial ?? false,
        totalCycles: input.totalCycles ?? 0,
        daysRepetition: input.daysRepetition ?? 0,
        notes: input.notes ?? null,
        id: prev.id,
        updatedAt: now,
        updatedBy: userId,
    };
    await tx.mutate.protocols.update(updatedProtocol);
    if (tx.location === 'server') {
        const patches = createPatch(prev, { ...prev, ...updatedProtocol });
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.protocol,
            resourceId: prev.id,
            action: 'update',
            domain: 'configuration',
            event: EventLogs.PROTOCOL_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            status: 'OK',
            details: {
                protocolName: updatedProtocol.name ?? prev.name,
                protocolId: prev.id,
            },
        });
    }
    return { prev, updatedProtocol };
}
export async function getProtocolPrescribedNames(tx, therapySequences) {
    const prescribedNameIds = [];
    for (const ts of therapySequences ?? []) {
        for (const med of ts.medicine ?? []) {
            if (med.prescribedNameId[0]) {
                prescribedNameIds.push(med.prescribedNameId[0]);
            }
        }
    }
    const prescribedNames = prescribedNameIds.length > 0
        ? await tx.run(zql.prescribedNames
            .related('drugCatalogItem')
            .where('id', 'IN', prescribedNameIds)
            .where('deletedAt', 'IS', null))
        : [];
    return prescribedNames;
}
export async function getProtocolTherapySequences(tx, protocolId) {
    const therapySequences = await tx.run(zql.protocolTherapySequence.related('medicine').where('protocolId', '=', protocolId).where('deletedAt', 'IS', null));
    return therapySequences;
}
export async function getAllProtocolTherapies(tx) {
    const therapySequences = await tx.run(zql.protocolTherapySequence.where('deletedAt', 'IS', null));
    return therapySequences;
}
export async function deleteUnusedTherapySequences(tx, userId, protocolId, therapySequences) {
    const now = Date.now();
    const protocol = await tx.run(zql.protocols.where('id', '=', protocolId).one());
    if (!protocol)
        throw new Error('Protocol not found');
    const existingSequences = await getProtocolTherapySequences(tx, protocol.id);
    const inputSequenceIds = new Set(therapySequences.map(ts => ts.id));
    const removedSequences = existingSequences.filter(seq => !inputSequenceIds.has(seq.id));
    await Promise.all(removedSequences.map(seq => tx.mutate.protocolTherapySequence.update({ id: seq.id, deletedAt: now, deletedBy: userId })));
    if (removedSequences.length > 0 && tx.location === 'server') {
        await Promise.all(removedSequences.map(seq => {
            const patches = createPatch(seq, { ...seq, deletedAt: now, deletedBy: userId });
            return addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.protocolTherapySequence,
                resourceId: seq.id,
                action: 'delete',
                domain: 'configuration',
                event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_DELETED,
                patches,
                performedBy: userId,
                performedAt: now,
                status: 'WARNING',
                details: {
                    protocolName: protocol.name,
                    protocolId: protocol.id,
                    ...itemsDetail(valueItem('day', seq.day)),
                },
            });
        }));
    }
}
export async function upsertTherapySequnce(tx, userId, protocolId, ts, eventDetails, existingSeq, names) {
    const now = Date.now();
    const sequenceData = {
        protocolId: protocolId,
        day: ts.day,
        time: ts.time,
        isNote: ts.isNote ?? false,
        note: ts.note ?? null,
        carrierSolutionId: ts.carrierSolutionId[0],
        applicationFormId: ts.applicationFormId[0],
        duration: ts.duration,
        volume: ts.volume,
        volumeMode: ts.volumeMode[0],
        therapyNote: ts.therapyNote,
        pharmacyNote: ts.pharmacyNote,
    };
    const therapySequenceId = ts.id ?? uuidv7();
    await tx.mutate.protocolTherapySequence.upsert({
        id: therapySequenceId,
        ...sequenceData,
        updatedAt: now,
        updatedBy: userId,
    });
    // the editor sends new days with an id too, so a day is new when it does not exist yet
    if (!existingSeq && tx.location === 'server') {
        const patches = createPatch({}, { ...sequenceData, id: therapySequenceId });
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.protocolTherapySequence,
            resourceId: therapySequenceId,
            action: 'create',
            domain: 'configuration',
            event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_ADDED,
            patches,
            performedBy: userId,
            performedAt: now,
            status: 'OK',
            details: {
                protocolId: protocolId,
                ...eventDetails,
                ...itemsDetail(valueItem('day', ts.day)),
            },
        });
    }
    if (ts.id && existingSeq && names && tx.location === 'server') {
        const items = protocolDayItems(existingSeq, sequenceData, {
            carrier: {
                old: names.drugName(existingSeq.carrierSolutionId),
                new: names.drugName(sequenceData.carrierSolutionId),
            },
            applicationForm: {
                old: names.formName(existingSeq.applicationFormId),
                new: names.formName(sequenceData.applicationFormId),
            },
        });
        if (items.length > 0) {
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.protocolTherapySequence,
                resourceId: therapySequenceId,
                action: 'update',
                domain: 'configuration',
                event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_UPDATED,
                patches: createPatch(existingSeq, { ...existingSeq, ...sequenceData }),
                performedBy: userId,
                performedAt: now,
                status: 'OK',
                details: { protocolId, ...eventDetails, ...itemsDetail(items) },
            });
        }
    }
    return therapySequenceId;
}
export async function updateProtocolTherapySequence(tx, userId, protocolId, ts, prescribedNames, eventDetails) {
    const now = Date.now();
    const existingSequences = await getProtocolTherapySequences(tx, protocolId);
    const existingSequenceMap = new Map(existingSequences.map(seq => [seq.id, seq]));
    const existingSeq = existingSequenceMap.get(ts.id);
    const existingMedicines = existingSeq?.medicine ?? [];
    const names = tx.location === 'server'
        ? await loadProtocolLogNames(tx, {
            drugIds: [
                ...existingMedicines.map(m => m.activeSubstanceId),
                ...ts.medicine.map(m => prescribedNames.find(pn => pn.id === m.prescribedNameId[0])?.drugCatalogItemId),
                ...ts.medicine.map(m => m.activeSubstanceId[0]),
                existingSeq?.carrierSolutionId,
                ts.carrierSolutionId[0],
            ],
            tradeNameIds: [
                ...existingMedicines.map(m => m.prescribedNameId),
                ...ts.medicine.map(m => m.prescribedNameId[0]),
            ],
            formIds: [existingSeq?.applicationFormId, ts.applicationFormId[0]],
        })
        : undefined;
    const therapySequenceId = await upsertTherapySequnce(tx, userId, protocolId, ts, { protocolName: eventDetails?.protocolName ?? '' }, existingSeq, names);
    const dayItems = valueItem('day', ts.day);
    // Handle medicines within this sequence
    const existingMedicineMap = new Map(existingMedicines.map(m => [m.id, m]));
    const inputMedicineIds = new Set(ts.medicine.filter(m => m.id).map(m => m.id ?? ''));
    // Soft-delete medicines that are no longer in the input
    const removedMedicines = existingMedicines.filter(m => !inputMedicineIds.has(m.id));
    await Promise.all(removedMedicines.map(m => tx.mutate.protocolTherapySequenceMedicine.update({ id: m.id, deletedAt: now, deletedBy: userId })));
    if (names) {
        await Promise.all(removedMedicines.map(m => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.protocolTherapySequenceMedicine,
            resourceId: m.id,
            action: 'delete',
            domain: 'configuration',
            event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_MEDICINE_REMOVED,
            patches: createPatch(m, { ...m, deletedAt: now, deletedBy: userId }),
            performedBy: userId,
            performedAt: now,
            status: 'WARNING',
            details: {
                protocolId,
                protocolName: eventDetails?.protocolName ?? '',
                ...itemsDetail([
                    ...dayItems,
                    ...valueItem('medicine', names.medicineName(m)),
                    ...valueItem('dose', m.regularDose, { unit: names.doseUnit(m.activeSubstanceId, m.calculationMethod) }),
                ]),
            },
        })));
    }
    const sequenceOps = [];
    for (const med of ts.medicine) {
        const prescribedName = prescribedNames.find(pn => pn.id === med.prescribedNameId[0]);
        if (med.prescribedNameId[0] && !prescribedName) {
            throw new Error(`Prescribed name with id ${med.prescribedNameId[0]} not found`);
        }
        const activeSubstanceId = prescribedName?.drugCatalogItemId ?? med.activeSubstanceId[0];
        const medicineData = {
            therapySequenceId: therapySequenceId,
            activeSubstanceId,
            prescribedNameId: med.prescribedNameId[0],
            autIdem: med.prescribedNameId[0] ? (med.autIdem ?? false) : false,
            calculationMethod: med.calculationMethod[0],
            regularDose: med.regularDose,
            precisionDecimals: med.precisionDecimals,
            notes: med.notes,
            ageRanges: med.ageRanges,
        };
        const existingMed = med.id ? existingMedicineMap.get(med.id) : undefined;
        if (existingMed) {
            const substanceChanged = (existingMed.activeSubstanceId ?? undefined) !== activeSubstanceId ||
                (existingMed.prescribedNameId ?? undefined) !== med.prescribedNameId[0];
            const doseChanged = existingMed.regularDose !== med.regularDose ||
                (existingMed.calculationMethod ?? undefined) !== med.calculationMethod[0];
            sequenceOps.push(() => tx.mutate.protocolTherapySequenceMedicine.update({
                id: existingMed.id,
                ...medicineData,
                updatedAt: now,
                updatedBy: userId,
            }));
            const newName = names?.medicineName({ activeSubstanceId, prescribedNameId: med.prescribedNameId[0] }) ?? '-';
            if (substanceChanged && names) {
                const patches = createPatch({
                    activeSubstanceId: existingMed.activeSubstanceId ?? undefined,
                    prescribedNameId: existingMed.prescribedNameId ?? undefined,
                }, {
                    activeSubstanceId,
                    prescribedNameId: med.prescribedNameId[0],
                });
                if (patches.length > 0) {
                    sequenceOps.push(() => addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.protocolTherapySequenceMedicine,
                        resourceId: existingMed.id,
                        action: 'update',
                        domain: 'configuration',
                        event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_SUBSTANCE_CHANGED,
                        patches,
                        performedBy: userId,
                        performedAt: now,
                        status: 'WARNING',
                        details: {
                            protocolId: protocolId,
                            protocolName: eventDetails?.protocolName ?? '',
                            ...itemsDetail([...dayItems, { field: 'medicine', old: names.medicineName(existingMed), new: newName }]),
                        },
                    }));
                }
            }
            if (doseChanged && names) {
                const patches = createPatch({
                    regularDose: existingMed.regularDose,
                    calculationMethod: existingMed.calculationMethod ?? undefined,
                }, {
                    regularDose: med.regularDose,
                    calculationMethod: med.calculationMethod[0],
                });
                if (patches.length > 0) {
                    sequenceOps.push(() => addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.protocolTherapySequenceMedicine,
                        resourceId: existingMed.id,
                        action: 'update',
                        domain: 'configuration',
                        event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_DOSE_CHANGED,
                        patches,
                        performedBy: userId,
                        performedAt: now,
                        status: 'INFO',
                        details: {
                            protocolId: protocolId,
                            protocolName: eventDetails?.protocolName ?? '',
                            ...itemsDetail([
                                ...dayItems,
                                ...changedItems(existingMed, { regularDose: med.regularDose, calculationMethod: med.calculationMethod[0] }, [
                                    {
                                        field: 'dose',
                                        key: 'regularDose',
                                        label: newName,
                                        unit: names.doseUnit(activeSubstanceId, med.calculationMethod[0]),
                                    },
                                    { field: 'calculation_method', key: 'calculationMethod' },
                                ]),
                            ]),
                        },
                    }));
                }
            }
        }
        else {
            const newMedicineId = uuidv7();
            sequenceOps.push(() => tx.mutate.protocolTherapySequenceMedicine.insert({
                id: newMedicineId,
                ...medicineData,
                createdAt: now,
                createdBy: userId,
            }));
            if (names) {
                const patches = createPatch({}, { ...medicineData, id: newMedicineId });
                sequenceOps.push(() => addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.protocolTherapySequenceMedicine,
                    resourceId: newMedicineId,
                    action: 'create',
                    domain: 'configuration',
                    event: EventLogs.PROTOCOL_THERAPY_SEQUENCE_MEDICINE_ADDED,
                    patches,
                    performedBy: userId,
                    performedAt: now,
                    status: 'OK',
                    details: {
                        protocolId: protocolId,
                        protocolName: eventDetails?.protocolName ?? '',
                        ...itemsDetail([
                            ...dayItems,
                            ...valueItem('medicine', names.medicineName(medicineData)),
                            ...valueItem('dose', med.regularDose, {
                                unit: names.doseUnit(activeSubstanceId, med.calculationMethod[0]),
                            }),
                        ]),
                    },
                }));
            }
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
}
export async function updateProtocolDaySettings(tx, dayZeroProtocol) {
    const therapies = await getAllProtocolTherapies(tx);
    const sequenceOps = [];
    for (const therapy of therapies) {
        const newDay = dayZeroProtocol ? Math.max(0, therapy.day - 1) : therapy.day + 1;
        sequenceOps.push(() => tx.mutate.protocolTherapySequence.update({
            id: therapy.id,
            day: newDay,
        }));
    }
    await Promise.all(sequenceOps.map(op => op()));
}
//# sourceMappingURL=protocols.js.map